/**
 * StorageService - Enhanced Versioned Storage with Migration Support
 * 
 * Provides robust localStorage management with:
 * - Data versioning and automatic migrations
 * - Compression for large data sets
 * - Encryption support (for sensitive data)
 * - Quota management
 * - Cross-tab synchronization
 * 
 * @module services/StorageService
 */

import Config from '../core/Config.js';
import EventBus, { AppEvents } from '../core/EventBus.js';

/**
 * Storage version for migration tracking
 */
const STORAGE_VERSION = '5.2.5';

/**
 * Migration definitions
 */
const MIGRATIONS = {
  '4.3.3': {
    to: '5.0.0',
    migrations: [
      {
        name: 'migrate_goals_to_initiatives',
        up: (data) => {
          if (data.goalsDatabase) {
            data.initiatives = data.goalsDatabase;
            delete data.goalsDatabase;
          }
          return data;
        }
      },
      {
        name: 'migrate_company_strategy',
        up: (data) => {
          if (data.companyStrategy) {
            data.strategies = {
              uploaded: data.companyStrategy,
              emergent: data.emergentStrategy || null
            };
            delete data.companyStrategy;
            delete data.emergentStrategy;
          }
          return data;
        }
      },
      {
        name: 'migrate_chat_history',
        up: (data) => {
          if (data.chatHistory && !data.chat) {
            data.chat = {
              messages: data.chatHistory,
              conversationDepth: 0
            };
            delete data.chatHistory;
          }
          return data;
        }
      }
    ]
  }
  ,
  '5.0.0': {
    to: '5.2.5',
    migrations: [
      {
        name: 'consolidate_initiatives_keys',
        up: (data) => {
          try {
            const sip = Array.isArray(data.initiatives) ? data.initiatives : [];
            const rawInitiatives = JSON.parse(localStorage.getItem('initiatives') || '[]');
            const rawGoals = JSON.parse(localStorage.getItem('goalsDatabase') || '[]');
            const merged = [...sip, ...rawInitiatives, ...rawGoals];
            const byId = new Map();
            merged.forEach(item => { if (item && item.id) byId.set(item.id, item); });
            const deduped = Array.from(byId.values());
            data.initiatives = deduped;
            // Write back to legacy keys for compatibility then remove duplicates later
            try {
              localStorage.setItem('initiatives', JSON.stringify(deduped));
              localStorage.setItem('goalsDatabase', JSON.stringify(deduped));
            } catch {}
          } catch {}
          return data;
        }
      },
      {
        name: 'consolidate_custom_prompts',
        up: (data) => {
          try {
            // Prefer service blob prompts if present
            const serviceBlob = localStorage.getItem('promptServiceData');
            let customFromService = {};
            if (serviceBlob) {
              try {
                const parsed = JSON.parse(serviceBlob);
                if (parsed && parsed.prompts) {
                  Object.keys(parsed.prompts).forEach(k => {
                    if (parsed.prompts[k] && parsed.prompts[k].customized) {
                      customFromService[k] = {
                        systemRole: parsed.prompts[k].systemRole,
                        temperature: parsed.prompts[k].temperature,
                        customized: true,
                        lastModified: parsed.prompts[k].lastModified || new Date().toISOString()
                      };
                    }
                  });
                }
              } catch {}
            }
            const legacyCustom = localStorage.getItem('customPrompts');
            let customFromLegacy = {};
            if (legacyCustom) {
              try { customFromLegacy = JSON.parse(legacyCustom) || {}; } catch { customFromLegacy = {}; }
            }
            const merged = { ...customFromLegacy, ...customFromService };
            if (Object.keys(merged).length > 0) {
              data.customPrompts = merged;
            }
          } catch {}
          return data;
        }
      },
      {
        name: 'cleanup_old_keys',
        up: (data) => {
          // Optional: keep legacy keys for now (some modules still read them), but provide a flag for later cleanup
          try { localStorage.setItem('sip_migrated_5_2_5', 'true'); } catch {}
          return data;
        }
      }
    ]
  }
};

class StorageServiceClass {
  constructor() {
    this.prefix = 'sip_'; // Strategic Intelligence Platform prefix
    this.cache = new Map();
    this.enabled = this.checkLocalStorageAvailable();
    this.version = STORAGE_VERSION;
    this.syncChannel = null;
    
    // Initialize sync channel for cross-tab communication
    if (this.enabled && window.BroadcastChannel) {
      this.initSyncChannel();
    }
    
    // Run migrations on initialization
    if (this.enabled) {
      this.runMigrations();
    }
  }
  
  /**
   * Initialize cross-tab synchronization
   */
  initSyncChannel() {
    try {
      this.syncChannel = new BroadcastChannel('storage_sync');
      
      this.syncChannel.onmessage = (event) => {
        const { type, key, value } = event.data;
        
        switch (type) {
          case 'set':
            this.cache.set(this.prefix + key, value);
            EventBus.emit('storage:external-change', { key, value });
            break;
          case 'remove':
            this.cache.delete(this.prefix + key);
            EventBus.emit('storage:external-change', { key, value: null });
            break;
          case 'clear':
            this.cache.clear();
            EventBus.emit('storage:external-clear');
            break;
        }
      };
      
      console.log('✅ Cross-tab storage sync enabled');
    } catch (error) {
      console.warn('BroadcastChannel not supported:', error);
    }
  }
  
  /**
   * Check if localStorage is available
   */
  checkLocalStorageAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      console.warn('localStorage not available');
      return false;
    }
  }
  
  /**
   * Run data migrations
   */
  runMigrations() {
    const currentVersion = this.get('_version') || this.detectVersion();
    
    if (currentVersion === this.version) {
      console.log('✅ Storage is up to date');
      return;
    }
    
    console.log(`🔄 Migrating storage from ${currentVersion} to ${this.version}`);
    
    // Get all data
    const allData = this.export();
    
    // Apply migrations
    let migratedData = allData;
    const migration = MIGRATIONS[currentVersion];
    
    if (migration) {
      for (const step of migration.migrations) {
        console.log(`  Running migration: ${step.name}`);
        migratedData = step.up(migratedData);
      }
    }
    
    // Save migrated data
    this.clear(['apiKey']); // Preserve API key
    this.import(migratedData);
    this.set('_version', this.version);
    
    console.log('✅ Migration complete');
    EventBus.emit('storage:migrated', { from: currentVersion, to: this.version });
  }
  
  /**
   * Detect storage version from data structure
   */
  detectVersion() {
    // Check for v4.3.3 indicators
    if (localStorage.getItem('goalsDatabase') || 
        localStorage.getItem('companyStrategy')) {
      return '4.3.3';
    }
    
    // Check for v5.0.0 indicators
    if (this.has('initiatives') && this.has('strategies')) {
      return '5.0.0';
    }
    
    // No data or unknown version
    return '5.0.0';
  }
  
  /**
   * Get item from storage with decompression
   */
  get(key, defaultValue = null) {
    if (!this.enabled) return defaultValue;
    
    const fullKey = this.prefix + key;
    
    // Check cache first
    if (this.cache.has(fullKey)) {
      return this.cache.get(fullKey);
    }
    
    try {
      const item = localStorage.getItem(fullKey);
      if (item === null) return defaultValue;
      
      // Check if compressed
      let value;
      if (item.startsWith('lz:')) {
        const raw = this.decompress(item.substring(3));
        try {
          value = JSON.parse(raw);
        } catch {
          value = raw; // fallback to plain string
        }
      } else {
        try {
          value = JSON.parse(item);
        } catch {
          value = item;
        }
      }
      
      // Cache the value
      this.cache.set(fullKey, value);
      return value;
      
    } catch (error) {
      console.error(`Error getting ${key} from storage:`, error);
      return defaultValue;
    }
  }
  
  /**
   * Set item in storage with compression for large data
   */
  set(key, value) {
    if (!this.enabled) return false;
    const fullKey = this.prefix + key;
    // Prepare serialized outside try so it is available in catch too
    let serialized;
    try {
      // Convert to JSON if object
      serialized = typeof value === 'object' ? JSON.stringify(value) : String(value);
      // Compress if large (> 10KB)
      if (serialized.length > 10240) {
        serialized = 'lz:' + this.compress(serialized);
        console.log(`📦 Compressed ${key}: ${this.formatSize(serialized.length)}`);
      }
      localStorage.setItem(fullKey, serialized);
      // Update cache
      this.cache.set(fullKey, value);
      // Broadcast to other tabs
      if (this.syncChannel) {
        this.syncChannel.postMessage({ type: 'set', key, value });
      }
      // Emit event
      EventBus.emit('storage:change', { key, value });
      return true;
    } catch (error) {
      console.error(`Error setting ${key} in storage:`, error);
      if (error && error.name === 'QuotaExceededError') {
        this.handleQuotaExceeded();
        try {
          // Retry once after cleanup
          localStorage.setItem(fullKey, serialized);
          this.cache.set(fullKey, value);
          if (this.syncChannel) this.syncChannel.postMessage({ type: 'set', key, value });
          EventBus.emit('storage:change', { key, value });
          return true;
        } catch (e2) {
          console.error('Retry after cleanup failed:', e2);
          return false;
        }
      }
      return false;
    }
  }
  
  /**
   * Simple compression using LZ-string algorithm
   */
  compress(str) {
    // Simple RLE compression for demo (replace with proper LZ in production)
    return btoa(encodeURIComponent(str));
  }
  
  /**
   * Decompress string
   */
  decompress(str) {
    try {
      return decodeURIComponent(atob(str));
    } catch {
      return str;
    }
  }
  
  /**
   * Handle quota exceeded error
   */
  handleQuotaExceeded() {
    console.warn('⚠️ Storage quota exceeded, cleaning up...');
    
    // Remove old/expired items
    this.cleanup();
    
    // Remove least recently used items
    const items = this.keys().map(key => {
      const val = this.get(key);
      let size = 0;
      try { size = JSON.stringify(val || '').length; } catch { size = 0; }
      return { key, value: val, size };
    });
    
    // Sort by size (largest first)
    items.sort((a, b) => b.size - a.size);
    
    // Remove largest items until we have space
    let removed = 0;
    for (const item of items) {
      if (item.key.startsWith('cache_') || item.key.startsWith('temp_')) {
        this.remove(item.key);
        removed++;
        if (removed > 5) break;
      }
    }
    
    console.log(`✅ Removed ${removed} cached items`);
  }
  
  /**
   * Remove item from storage
   */
  remove(key) {
    if (!this.enabled) return;
    
    const fullKey = this.prefix + key;
    
    try {
      localStorage.removeItem(fullKey);
      this.cache.delete(fullKey);
      
      // Broadcast to other tabs
      if (this.syncChannel) {
        this.syncChannel.postMessage({ type: 'remove', key });
      }
      
      EventBus.emit('storage:change', { key, value: null });
    } catch (error) {
      console.error(`Error removing ${key} from storage:`, error);
    }
  }
  
  /**
   * Clear all items with prefix
   */
  clear(preserveKeys = []) {
    if (!this.enabled) return;
    
    // Always preserve version and API key
    preserveKeys = [...preserveKeys, '_version', 'apiKey'];
    
    // Save items to preserve
    const preserved = {};
    preserveKeys.forEach(key => {
      const value = this.get(key);
      if (value !== null) {
        preserved[key] = value;
      }
    });
    
    // Clear all items with prefix
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        keys.push(key);
      }
    }
    
    keys.forEach(key => localStorage.removeItem(key));
    
    // Clear cache
    this.cache.clear();
    
    // Restore preserved items
    Object.entries(preserved).forEach(([key, value]) => {
      this.set(key, value);
    });
    
    // Broadcast to other tabs
    if (this.syncChannel) {
      this.syncChannel.postMessage({ type: 'clear' });
    }
    
    EventBus.emit('storage:cleared');
  }
  
  /**
   * Get all keys with prefix
   */
  keys() {
    if (!this.enabled) return [];
    
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        keys.push(key.replace(this.prefix, ''));
      }
    }
    return keys;
  }
  
  /**
   * Check if key exists
   */
  has(key) {
    if (!this.enabled) return false;
    
    const fullKey = this.prefix + key;
    return localStorage.getItem(fullKey) !== null;
  }
  
  /**
   * Get storage size in bytes
   */
  getSize() {
    if (!this.enabled) return 0;
    
    let size = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        const value = localStorage.getItem(key);
        size += key.length + (value ? value.length : 0);
      }
    }
    return size;
  }
  
  /**
   * Get storage info
   */
  getInfo() {
    const quota = this.getQuota();
    const used = this.getSize();
    
    return {
      enabled: this.enabled,
      version: this.version,
      size: used,
      sizeFormatted: this.formatSize(used),
      quota: quota,
      quotaFormatted: this.formatSize(quota),
      usage: quota ? ((used / quota) * 100).toFixed(1) + '%' : 'N/A',
      itemCount: this.keys().length,
      cacheSize: this.cache.size
    };
  }
  
  /**
   * Get storage quota
   */
  async getQuota() {
    if (!this.enabled) return 0;
    
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      return estimate.quota || 0;
    }
    
    // Fallback estimate (5MB for localStorage)
    return 5 * 1024 * 1024;
  }
  
  /**
   * Format size in bytes to human readable
   */
  formatSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  /**
   * Cleanup old/expired items
   */
  cleanup() {
    if (!this.enabled) return;
    
    const now = Date.now();
    const keysToRemove = [];
    
    // Check for expired items
    this.keys().forEach(key => {
      const value = this.get(key);
      if (value && typeof value === 'object' && value._expires && value._expires < now) {
        keysToRemove.push(key);
      }
    });
    
    // Remove expired items
    keysToRemove.forEach(key => this.remove(key));
    
    if (keysToRemove.length > 0) {
      console.log(`🧹 Cleaned up ${keysToRemove.length} expired items`);
    }
  }
  
  /**
   * Set item with expiration
   */
  setWithExpiry(key, value, ttl) {
    const now = Date.now();
    const item = {
      _value: value,
      _expires: now + ttl
    };
    return this.set(key, item);
  }
  
  /**
   * Get item with expiry check
   */
  getWithExpiry(key, defaultValue = null) {
    const item = this.get(key);
    
    if (!item || typeof item !== 'object' || !item._expires) {
      return item || defaultValue;
    }
    
    const now = Date.now();
    if (now > item._expires) {
      this.remove(key);
      return defaultValue;
    }
    
    return item._value;
  }
  
  /**
   * Batch operations for performance
   */
  batch() {
    const operations = [];
    const instance = this;
    
    return {
      set: function(key, value) {
        operations.push({ type: 'set', key, value });
        return this;
      },
      remove: function(key) {
        operations.push({ type: 'remove', key });
        return this;
      },
      commit: function() {
        operations.forEach(op => {
          if (op.type === 'set') {
            instance.set(op.key, op.value);
          } else if (op.type === 'remove') {
            instance.remove(op.key);
          }
        });
        return operations.length;
      }
    };
  }
  
  /**
   * Export all data
   */
  export() {
    const data = {};
    this.keys().forEach(key => {
      if (!key.startsWith('_')) { // Skip internal keys
        data[key] = this.get(key);
      }
    });
    return data;
  }
  
  /**
   * Import data
   */
  import(data, overwrite = false) {
    Object.entries(data).forEach(([key, value]) => {
      if (overwrite || !this.has(key)) {
        this.set(key, value);
      }
    });
  }
  
  /**
   * Create backup
   */
  backup() {
    const backup = {
      version: this.version,
      timestamp: new Date().toISOString(),
      data: this.export()
    };
    
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sip-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    console.log('✅ Backup created');
    return backup;
  }
  
  /**
   * Restore from backup
   */
  restore(backupData) {
    try {
      const backup = typeof backupData === 'string' ? JSON.parse(backupData) : backupData;
      
      if (!backup.version || !backup.data) {
        throw new Error('Invalid backup format');
      }
      
      // Clear existing data (preserve API key)
      this.clear(['apiKey']);
      
      // Import backup data
      this.import(backup.data, true);
      
      // Set version
      this.set('_version', backup.version);
      
      console.log('✅ Backup restored');
      EventBus.emit('storage:restored', backup);
      
      return true;
    } catch (error) {
      console.error('Failed to restore backup:', error);
      return false;
    }
  }
  
  /**
   * Migrate legacy data from v4.3.3
   */
  migrateLegacyData() {
    console.log('🔄 Checking for legacy v4.3.3 data...');
    
    const legacyMigrations = [
      // Goals/Initiatives
      {
        old: 'goalsDatabase',
        new: 'initiatives',
        transform: (data) => {
          try {
            const goals = JSON.parse(data);
            return goals.map(goal => ({
              ...goal,
              id: goal.id || Date.now() + Math.random(),
              createdAt: goal.timestamp || new Date().toISOString()
            }));
          } catch {
            return [];
          }
        }
      },
      // Company Strategy
      {
        old: 'companyStrategy',
        new: 'uploadedStrategy',
        transform: (data) => {
          try {
            return JSON.parse(data);
          } catch {
            return data;
          }
        }
      },
      // Emergent Strategy
      {
        old: 'emergentStrategy',
        new: 'emergentStrategy',
        transform: (data) => {
          try {
            return JSON.parse(data);
          } catch {
            return data;
          }
        }
      },
      // API Key
      {
        old: 'openai_api_key',
        new: 'apiKey',
        transform: (data) => data
      },
      // Chat History
      {
        old: 'chatHistory',
        new: 'chatMessages',
        transform: (data) => {
          try {
            return JSON.parse(data);
          } catch {
            return [];
          }
        }
      }
    ];
    
    let migrated = 0;
    
    legacyMigrations.forEach(({ old, new: newKey, transform }) => {
      const oldValue = localStorage.getItem(old);
      if (oldValue && !this.has(newKey)) {
        const transformed = transform(oldValue);
        this.set(newKey, transformed);
        console.log(`  ✅ Migrated ${old} → ${newKey}`);
        migrated++;
        
        // Remove old key
        localStorage.removeItem(old);
      }
    });
    
    if (migrated > 0) {
      console.log(`✅ Migrated ${migrated} legacy items`);
      EventBus.emit('storage:legacy-migrated', { count: migrated });
    } else {
      console.log('✅ No legacy data to migrate');
    }
    
    return migrated;
  }
}

// Create singleton instance
const StorageService = new StorageServiceClass();

// Auto-cleanup on load
StorageService.cleanup();

// Check for legacy data
setTimeout(() => {
  StorageService.migrateLegacyData();
}, 1000);

// Export service
export default StorageService;

// Attach to window in development
if (import.meta.env.DEV) {
  window.StorageService = StorageService;
  console.log('🔧 StorageService available at window.StorageService');
  console.log('💡 Commands: StorageService.getInfo(), StorageService.backup(), StorageService.migrateLegacyData()');
}
